import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { StatusIndicator, PriorityBadge } from '@/modules/wms/components/common/StatusIndicator';
import { BarcodeScanner } from '@/shared/components/scanner/BarcodeScanner';
import { CameraScanner } from '@/shared/components/scanner/CameraScanner';
import { ScanWorkflowDialog } from '@/modules/wms/components/scanner/ScanWorkflowDialog';
import { pickingWorkflowSteps, samplePickingItems } from '@/modules/wms/lib/scanWorkflowConfig';
import { pickingAPI, salesAPI } from '@/shared/services/api';
import {
  Search,
  Package,
  ClipboardList,
  CheckCircle2,
  Clock,
  ArrowRight,
  ScanLine,
  Play,
  ListChecks,
  MapPin,
  ChevronRight,
  Loader2,
  Camera,
  Keyboard } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';
import { toast } from 'sonner';

export const Picking = () => {const { t } = useTranslation("wms");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderStatuses, setOrderStatuses] = useState({});
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ readyToPick: 0, inProgress: 0, completed: 0 });
  const [scanMode, setScanMode] = useState('manual'); // 'manual' or 'camera'

  // Fetch orders from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [tasksData, statsData] = await Promise.all([
        pickingAPI.getTasks(),
        pickingAPI.getStats()]
        );
        // Get orders ready for picking
        const ordersData = await salesAPI.getOrders({ status: 'allocated' }).catch(() => []);
        const allOrders = [...(ordersData || []), ...(tasksData || [])];
        setOrders(allOrders);
        setStats({
          readyToPick: statsData?.pending || allOrders.filter((o) => o.status === 'ready_to_pick' || o.status === 'allocated').length,
          inProgress: statsData?.in_progress || allOrders.filter((o) => o.status === 'picking').length,
          completed: statsData?.completed_today || 0
        });
      } catch (error) {
        console.error('Failed to fetch picking data:', error);
        toast.error('Failed to load picking tasks');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle quick scan
  const handleQuickScan = async (result) => {
    if (result.valid) {
      // Check if it's an internal order scan
      if (result.type === 'INTERNAL' && result.raw.startsWith('SO-')) {
        const matchingOrder = orders.find((o) => o.id === result.raw || o.order_number === result.raw || o.status === 'ready_to_pick' || o.status === 'allocated');
        if (matchingOrder) {
          setSelectedOrder(matchingOrder);
          setShowWorkflowDialog(true);
          toast.success('Order Found', {
            description: `Starting pick for ${matchingOrder.id || matchingOrder.order_number} - ${matchingOrder.items?.length || matchingOrder.item_count || 0} items`
          });
        }
      } else {
        toast.info('Scan Recognized', {
          description: `${result.type}: ${result.raw}`
        });
      }
    }
  };

  // Start picking workflow
  const handleStartPicking = (order) => {
    setSelectedOrder(order);
    setShowWorkflowDialog(true);
  };

  // Handle workflow completion
  const handleWorkflowComplete = async (data) => {
    if (selectedOrder) {
      try {
        const orderId = selectedOrder.id || selectedOrder.order_number || selectedOrder.order_id;
        await pickingAPI.completePicking(orderId);
        setOrderStatuses((prev) => ({
          ...prev,
          [orderId]: 'packing'
        }));
        // Refresh orders
        const tasksData = await pickingAPI.getTasks();
        setOrders(tasksData || []);
        toast.success('Picking Complete', {
          description: `${data.scannedItems.length} items picked for ${orderId}. Ready for packing.`
        });
      } catch (error) {
        console.error('Failed to complete picking:', error);
        toast.error('Failed to complete picking');
      }
    }
  };

  // Handle step completion
  const handleStepComplete = (stepIndex, scanResult) => {
    const step = pickingWorkflowSteps[stepIndex];

    if (step.triggersAction === 'load_pick_list') {
      toast.info('Pick List Loaded', {
        description: 'Navigate to first pick location'
      });
    } else if (step.triggersAction === 'verify_location') {
      toast.success('Location Verified', {
        description: `At ${scanResult.raw}`
      });
    } else if (step.triggersAction === 'confirm_pick') {
      toast.success('Item Picked', {
        description: 'Item verified and confirmed'
      });
    }
  };

  // Get effective status
  const getOrderStatus = (order) => {
    const orderId = order.id || order.order_number || order.order_id;
    return orderStatuses[orderId] || order.status;
  };

  // Filter orders for display
  const displayOrders = orders.filter((o) => {
    const status = o.status;
    return status === 'ready_to_pick' || status === 'picking' || status === 'allocated' || status === 'pending';
  });

  return (
    <div className="space-y-6" data-testid="wms-picking">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("picking", "Picking")}</h1>
          <p className="text-muted-foreground mt-1">{t("scanguidedOrderFulfillment", "Scan-guided order fulfillment and picking tasks")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <ListChecks className="h-4 w-4" />{t("wavePicking", "Wave Picking")}

          </Button>
          <Button variant="outline" className="gap-2" onClick={() => setShowWorkflowDialog(true)}>
            <ScanLine className="h-4 w-4" />{t("startScanWorkflow", "Start Scan Workflow")}

          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("readyToPick", "Ready to Pick")}</p>
              <p className="text-2xl font-bold text-blue-600">{stats.readyToPick}</p>
            </div>
            <ClipboardList className="h-8 w-8 text-blue-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("inProgress", "In Progress")}</p>
              <p className="text-2xl font-bold text-amber-600">{stats.inProgress}</p>
            </div>
            <Clock className="h-8 w-8 text-amber-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-green-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("completedToday", "Completed Today")}</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            </div>
            <CheckCircle2 className="h-8 w-8 text-green-500/30" />
          </CardContent>
        </Card>
      </div>

      {/* Scan-Gated Picking Input */}
      <Card className="card-elevated bg-gradient-to-r from-green-50/50 to-transparent dark:from-green-950/20">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/50">
              <ScanLine className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold">{t("scanguidedPicking", "Scan-Guided Picking")}</h3>
              <p className="text-sm text-muted-foreground">{t("scanOrderBarcode", "Scan order barcode to load pick list and start guided picking")}

              </p>
            </div>
            <Badge variant="outline" className="ml-auto gap-1">
              <Package className="h-3 w-3" />{t("scannerguided", "Scanner-Guided")}

            </Badge>
          </div>
          
          <Tabs value={scanMode} onValueChange={setScanMode} className="mb-4">
            <TabsList className="grid w-full max-w-xs grid-cols-2">
              <TabsTrigger value="manual" className="gap-2">
                <Keyboard className="h-4 w-4" />{t("manual", "Manual")}

              </TabsTrigger>
              <TabsTrigger value="camera" className="gap-2">
                <Camera className="h-4 w-4" />{t("camera", "Camera")}

              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {scanMode === 'manual' ?
          <BarcodeScanner
            onScan={handleQuickScan}
            scanMode="internal"
            placeholder="Scan order barcode (SO-XXXX-XXXX) to start picking..."
            showSamples /> :


          <CameraScanner
            onScan={(barcode) => handleQuickScan({ valid: true, raw: barcode })}
            showControls={true} />

          }
        </CardContent>
      </Card>

      {/* Workflow Steps Guide */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <h4 className="font-medium mb-3">{t("scangatedPickingProcess", "Scan-Gated Picking Process")}</h4>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">1</div>
              <span>{t("scanOrder", "Scan Order")}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-2 px-3 py-2 bg-violet-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-violet-500 text-white flex items-center justify-center text-xs font-bold">2</div>
              <span>{t("verifyLocation", "Verify Location")}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold">3</div>
              <span>{t("scanItem", "Scan Item")}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span>{t("complete", "Complete")}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders List */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg">{t("ordersReadyFor", "Orders Ready for Picking")}</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by order ID or customer"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("orderId", "Order ID")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("customer", "Customer")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("dueDate", "Due Date")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("items", "Items")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("priority", "Priority")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ?
                <tr>
                    <td colSpan={7} className="py-8 text-center">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mt-2">{t("loadingOrders", "Loading orders...")}</p>
                    </td>
                  </tr> :
                displayOrders.length === 0 ?
                <tr>
                    <td colSpan={7} className="py-8 text-center">
                      <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                      <p className="text-sm text-muted-foreground">{t("noOrdersReady", "No orders ready for picking")}</p>
                    </td>
                  </tr> :
                displayOrders.map((order) => {
                  const orderId = order.id || order.order_number || order.order_id;
                  const effectiveStatus = getOrderStatus(order);
                  return (
                    <tr key={orderId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-4 px-4">
                        <span className="font-mono text-sm font-medium">{orderId}</span>
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <p className="text-sm font-medium">{order.customer || order.customer_name || '-'}</p>
                          <p className="text-xs text-muted-foreground">{order.shippingMethod || order.shipping_method || '-'}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm">{order.dueDate || order.due_date || '-'}</span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="font-medium">{order.items?.length || order.item_count || 0}{t("items", "items")}</span>
                        <p className="text-xs text-muted-foreground">{order.totalUnits || order.total_qty || 0}{t("units", "units")}</p>
                      </td>
                      <td className="py-4 px-4">
                        <PriorityBadge priority={order.priority || 'medium'} />
                      </td>
                      <td className="py-4 px-4">
                        <StatusIndicator status={effectiveStatus} />
                      </td>
                      <td className="py-4 px-4 text-right">
                        <Button
                          size="sm"
                          onClick={() => handleStartPicking(order)}
                          className={effectiveStatus === 'picking' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                          data-testid={`process-order-${orderId}`}>
                          
                          {(effectiveStatus === 'ready_to_pick' || effectiveStatus === 'allocated') &&
                          <>
                              <ScanLine className="h-4 w-4 mr-1" />{t("startPick", "Start Pick")}

                          </>
                          }
                          {effectiveStatus === 'picking' && 'Continue'}
                          {effectiveStatus === 'packing' && 'View'}
                          <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                      </td>
                    </tr>);

                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-lg">{t("recentActivity", "Recent Activity")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50/50 dark:bg-green-950/20">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <div className="flex-1">
                <p className="text-sm font-medium">{t("orderOrd12340Completed", "Order ORD-12340 completed")}</p>
                <p className="text-xs text-muted-foreground">{t("12ItemsPicked", "12 items picked - John Smith - 11:30 AM")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50/50 dark:bg-amber-950/20">
              <Clock className="h-5 w-5 text-amber-600" />
              <div className="flex-1">
                <p className="text-sm font-medium">{t("insufficientStockAlert", "Insufficient stock alert")}</p>
                <p className="text-xs text-muted-foreground">{t("skuc3003Only130", "SKU-C3003 - Only 130 units available - 11:15 AM")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50/50 dark:bg-green-950/20">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <div className="flex-1">
                <p className="text-sm font-medium">{t("orderOrd12339Completed", "Order ORD-12339 completed")}</p>
                <p className="text-xs text-muted-foreground">{t("8ItemsPicked", "8 items picked - Sarah Johnson - 10:45 AM")}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scan Workflow Dialog */}
      <ScanWorkflowDialog
        open={showWorkflowDialog}
        onOpenChange={setShowWorkflowDialog}
        title={selectedOrder ? `Picking: ${selectedOrder.id}` : 'Picking Workflow'}
        icon={Package}
        steps={pickingWorkflowSteps}
        expectedItems={samplePickingItems}
        onComplete={handleWorkflowComplete}
        onStepComplete={handleStepComplete}
        validationMode="flexible" />
      
    </div>);

};